import { Link, Outlet, useLocation } from "react-router-dom"
import styles from "./styles.module.scss"
import {
  DISCOVERED_TRANSLATION_KEYS,
  LANG_KEYS,
  ROUTER_PATHS,
} from "@/shared/constants"
import {
  ArrowLeftCircle,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  CreditCard,
  Mic,
  Mail,
  Tag,
  Lock,
  Trash2,
  MonitorSmartphone,
  UserRoundPlus,
  Languages,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useCallback, useEffect, useRef, useState } from "react"
import { useAutoTranslation, useUserStore } from "@/entities"

const NAVIGATION_ITEMS = [
  {
    label: DISCOVERED_TRANSLATION_KEYS.CONNECT_EMAIL,
    path: ROUTER_PATHS.PROFILE_CONNECT_EMAIL,
    icon: UserRoundPlus,
    guestOnly: true,
  },
  {
    label: LANG_KEYS.ACTIVE_SESSIONS,
    path: ROUTER_PATHS.PROFILE_AUTH_SESSIONS,
    icon: MonitorSmartphone,
  },

  {
    label: DISCOVERED_TRANSLATION_KEYS.CONTACT_US,
    path: ROUTER_PATHS.PROFILE_CONTACT_US,
    icon: HelpCircle,
  },
  {
    label: DISCOVERED_TRANSLATION_KEYS.MANAGE_SUBSCRIPTION,
    path: ROUTER_PATHS.PROFILE_MANAGE_SUBSCRIPTION,
    icon: CreditCard,
  },
  {
    label: LANG_KEYS.CHANGE_VOICE,
    path: ROUTER_PATHS.PROFILE_CHANGE_VOICE,
    icon: Mic,
  },
  {
    label: LANG_KEYS.LANGUAGE,
    path: ROUTER_PATHS.PROFILE_LANGUAGE,
    icon: Languages,
  },
  {
    label: DISCOVERED_TRANSLATION_KEYS.CHANGE_EMAIL,
    path: ROUTER_PATHS.PROFILE_CHANGE_EMAIL,
    icon: Mail,
  },
  {
    label: DISCOVERED_TRANSLATION_KEYS.APPLY_PROMO_CODE,
    path: ROUTER_PATHS.PROFILE_APPLY_PROMO_CODE,
    icon: Tag,
  },
  {
    label: DISCOVERED_TRANSLATION_KEYS.CHANGE_PASSWORD,
    path: ROUTER_PATHS.PROFILE_CHANGE_PASSWORD,
    icon: Lock,
  },
  {
    label: DISCOVERED_TRANSLATION_KEYS.DELETE_ACCOUNT,
    path: ROUTER_PATHS.PROFILE_DELETE_ACCOUNT,
    icon: Trash2,
  },
]

const GUEST_HIDDEN_PATHS = new Set([
  ROUTER_PATHS.PROFILE_MANAGE_SUBSCRIPTION,
  ROUTER_PATHS.PROFILE_CHANGE_EMAIL,
  ROUTER_PATHS.PROFILE_CHANGE_PASSWORD,
])

export const LayoutProfile = () => {
  const { t } = useAutoTranslation()
  const isGuest = useUserStore(state => state.isGuest)
  const location = useLocation()
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateMenuScrollState = useCallback(() => {
    const el = menuRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    updateMenuScrollState()
    const el = menuRef.current
    if (!el) return

    el.addEventListener("scroll", updateMenuScrollState, { passive: true })
    window.addEventListener("resize", updateMenuScrollState)

    return () => {
      el.removeEventListener("scroll", updateMenuScrollState)
      window.removeEventListener("resize", updateMenuScrollState)
    }
  }, [updateMenuScrollState])

  const scrollMenuBy = (delta: number) => {
    menuRef.current?.scrollBy({ left: delta, behavior: "smooth" })
  }

  return (
    <div className={styles.page}>
      <div className={styles.page__inner}>
        <div className={styles.page__inner__left}>
          <Link to={ROUTER_PATHS.APP}>
            <ArrowLeftCircle />
          </Link>
        </div>
        <div className={styles.card}>
          <div className={styles.navigation}>
            <div className={styles.navigation__scrollRow}>
              <button
                type="button"
                aria-label={t(
                  DISCOVERED_TRANSLATION_KEYS.SCROLL_PROFILE_MENU_LEFT
                )}
                className={styles.navigation__chevron}
                onClick={() => scrollMenuBy(-240)}
                disabled={!canScrollLeft}
              >
                <ChevronLeft />
              </button>

              <div ref={menuRef} className={styles.navigation__listWrapper}>
                <ul>
                  {NAVIGATION_ITEMS.filter(
                    item =>
                      (isGuest || !item.guestOnly) &&
                      (!isGuest || !GUEST_HIDDEN_PATHS.has(item.path))
                  ).map(item => (
                    <li key={`NAVIGATION_ITEM_${item.path}`}>
                      <Link
                        to={item.path}
                        className={cn(styles.navigation__item, {
                          [styles.navigation__item_active]:
                            location.pathname === item.path,
                        })}
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        {t(item.label)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                type="button"
                aria-label={t(
                  DISCOVERED_TRANSLATION_KEYS.SCROLL_PROFILE_MENU_RIGHT
                )}
                className={styles.navigation__chevron}
                onClick={() => scrollMenuBy(240)}
                disabled={!canScrollRight}
              >
                <ChevronRight />
              </button>
            </div>
          </div>

          <div className={styles.content}>
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  )
}
